import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { Calendar, Map, Mail, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Profile from '../assets/img/asma1.png';
import { useTranslation } from '../contexts/LanguageContext';


const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const experiences = [
    {
      id: 1,
      title: t('about.experience.role'),
      company: t('about.experience.company'),
      period: t('about.experience.period'),
      description: t('about.experience.description'),
    }
  ];

  const education = [
    {
      id: 1,
      degree: t('about.education.degree'),
      institution: t('about.education.institution'),
      period: t('about.education.period'),
      description: t('about.education.description'),
    }
   
  ];

  return (
    <>
      <SEO
        title={t('about.seo.title')}
        description={t('about.seo.description')}
        keywords={['about me', 'developer background', 'work experience', 'education']}
        ogUrl="/about"
      />

      <div className="max-w-4xl mx-auto mt-10">
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl font-bold text-white mb-6">
              {t('about.title')}
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
              <div className="md:col-span-1">
                <div className="rounded-xl overflow-hidden shadow-md">
                  <img
                    src={Profile}
                    alt="Profile"
                    className="w-full h-auto object-cover"
                  />
                </div>
                
                <div className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6 mt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    {t('about.quickInfo.title')}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-center text-primary-100">
                      <Map size={18} className="mr-2 text-sky-600" />
                      {t('about.quickInfo.location')}
                    </li>
                    <li className="flex items-center text-primary-100">
                      <Mail size={18} className="mr-2 text-sky-600" />
                      <a href="mailto:asmaessafi137@gmail.com" className="hover:text-sky-700 transition-colors">
                        asmaessafi137@gmail.com
                      </a>
                    </li>
                    <li className="flex items-center text-primary-100">
                      <Calendar size={18} className="mr-2 text-sky-600" />
                      {t('about.quickInfo.available')}
                    </li>
                  </ul>
                  
                  <div className="mt-6">
                  <button
              className="bg-gradient-to-r from-primary-200 via-white to-primary-300 text-sky-600 font-bold px-5 py-2  rounded-xl shadow-xl  hover:shadow-sky-600  hover:from-primary-300 hover:via-primary-100 hover:thover:scale-[1.03] active:scale-95 transition-all duration-500 group text-lg mx-auto">
                    <a
                      href="https://flowcv.com/resume/1j37uwhopbdj"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('about.quickInfo.viewResume')}
                    </a>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-2">
                <div className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6 md:p-8">
                  <h2 className="text-2xl font-semibold text-white mb-4">
                    {t('about.journey.title')}
                  </h2>
                  <p className="text-primary-100 mb-4">
                    {t('about.journey.p1')}
                  </p>
                  <p className="text-primary-100 mb-4">
                    {t('about.journey.p2')}
                  </p>
                  <p className="text-primary-100">
                    {t('about.journey.p3')}
                  </p>
                  
                  <div className="mt-8">
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {t('about.values.title')}
                    </h3>
                    <ul className="space-y-2 text-primary-100">
                      <li className="flex items-start">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 text-sky-700 rounded-full mr-3 mt-0.5">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                        <span>{t('about.values.cleanCode')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 text-sky-700 rounded-full mr-3 mt-0.5">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                        <span>{t('about.values.userCentered')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 text-sky-700 rounded-full mr-3 mt-0.5">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                        <span>{t('about.values.learning')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 text-sky-700 rounded-full mr-3 mt-0.5">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                        <span>{t('about.values.accessible')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-white mb-8">
              {t('about.experience.title')}
            </h2>
            <div className="space-y-6">
              {experiences.map((exp, index) => (
                <motion.div
                  key={exp.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white">{exp.title}</h3>
                      <p className="text-sky-600 font-semibold">{exp.company}</p>
                    </div>
                    <div className="text-lime-400 text-sm mt-2 sm:mt-0">{exp.period}</div>
                  </div>
                  <p className="text-primary-100">{exp.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-white mb-8">
              {t('about.education.title')}
            </h2>
            <div className="space-y-6">
              {education.map((edu, index) => (
                <motion.div
                  key={edu.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white">{edu.degree}</h3>
                      <p className="text-sky-600 font-semibold">{edu.institution}</p>
                    </div>
                    <div className="text-lime-400 text-sm mt-2 sm:mt-0">{edu.period}</div>
                  </div>
                  <p className="text-primary-100">{edu.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        <section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-sky-300 to-sky-700 rounded-2xl p-8 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('about.cta.title')}
            </h2>
            <p className="text-white text-opacity-90 max-w-2xl mx-auto mb-8">
              {t('about.cta.text')}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center"
            >
              <button
              className="bg-gradient-to-r from-primary-200 via-white to-primary-300 hover:from-primary-300 hover:via-primary-100 hover:to-primary-400 text-sky-600 font-bold px-7 py-3  rounded-2xl shadow-xl  hover:shadow-sky-700 hover:scale-[1.03] active:scale-95 transition-all duration-500 group text-lg flex items-center mx-auto">
              {t('about.cta.button')}
              <ArrowRight className="ml-4 h-6 w-6 transition-transform group-hover:translate-x-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
            </button>
            </Link>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;